import { confirmSignIn } from "aws-amplify/auth";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { TranslatedText, getValidationRule } from "../../../helpers/helpers";
import { hideLoading, showLoading } from "../../../reducers/app";
import PasswordModalInfoMatch from "../../common/PasswordModalInfoMatch/PasswordModalInfoMatch";
import "./ChangePasswordChallenge.scss";

const ChangePasswordChallenge = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const validPasswordMinNumber = getValidationRule(
    "passwordMinimumNumberOfChar"
  );
  const validPaswordUppercase = getValidationRule("passwordUppercaseChar");
  const validPaswordLowercase = getValidationRule("passwordLowercaseChar");
  const validPaswordNumeric = getValidationRule("passwordNumericChar");
  const validPaswordSpecial = getValidationRule("passwordSpecialChar");

  let schemaValidation = Yup.object().shape({
    newPassword: Yup.string()
      .matches(validPasswordMinNumber, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .matches(validPaswordUppercase, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .matches(validPaswordLowercase, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .matches(validPasswordMinNumber, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .matches(validPaswordNumeric, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .matches(validPaswordSpecial, {
        message: (
          <TranslatedText i18nKey="errors.change.password.validation.match" />
        ) as any,
      })
      .required(
        (
          <TranslatedText i18nKey="errors.registration.password.required" />
        ) as any
      ),
    newPasswordAgain: Yup.string()
      .required(
        (
          <TranslatedText i18nKey="errors.registration.password.required" />
        ) as any
      )
      .oneOf(
        [Yup.ref("newPassword"), ""],
        (
          <TranslatedText i18nKey="error.changePassword.password.combine" />
        ) as any
      ),
  });
  const [open, setOpen] = useState(false);

  const handleChangePassword = async (
    values: any,
    { setErrors }: { setErrors: any }
  ) => {
    dispatch(showLoading());

    try {
      const attributes = props.user?.challengeParam?.requiredAttributes;

      const confirmOptions = attributes
        ? { userAttributes: attributes }
        : undefined;

      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: values.newPassword,
        options: confirmOptions,
      });

      console.info("Change Password Result:", isSignedIn, nextStep);

      if (isSignedIn) {
        window.location.href = `${process.env.PUBLIC_URL}`;
      } else {
        console.warn(
          "Ulteriore step richiesto dopo il cambio password:",
          nextStep
        );
      }
    } catch (error: any) {
      console.error("Change Password Error:", error);

      setErrors({ password: "Network Error" });
      dispatch(hideLoading());
    }
  };
  return (
    <div
      className="ChangePasswordChallenge"
      data-testid="ChangePasswordChallenge"
    >
      <div className={"content-holder"}>
        <div className="content-holder">
          <h1 className="rectangle headline-fs5">
            <span>{t("change.password.header")}</span>
          </h1>
          <h2 className="paragraph-small-fs9">
            {t("change.password.description")}
          </h2>
        </div>
        <Formik
          initialValues={{
            newPassword: "",
            newPasswordAgain: "",
          }}
          validationSchema={schemaValidation}
          onSubmit={(value, props) => handleChangePassword(value, props)}
        >
          {({ errors, touched }) => (
            <Form noValidate>
              <div className="row">
                <div className="col-xs-12 col-md-12">
                  <div className="row">
                    <div className="col-lg-12 col-md-12">
                      <div className="form-group">
                        <label
                          className="paragraph-small-fs9"
                          htmlFor={"newPassword"}
                        >
                          {t("change.password.newPassword")} *
                          <button
                            type="button"
                            className={"form-info-button"}
                            aria-label={t("aria.form.info.button")}
                            aria-haspopup="dialog"
                            aria-controls={"newPassword-info-modal"}
                            onClick={() => setOpen(true)}
                          >
                            <span
                              className="form-icon-info"
                              aria-hidden="true"
                            ></span>
                          </button>
                        </label>

                        <Field
                          name={"newPassword"}
                          type="password"
                          id={"newPassword"}
                          aria-describedby={
                            errors.newPassword && touched.newPassword
                              ? "newPassword-error"
                              : null
                          }
                          required={true}
                          className={
                            errors.newPassword && touched.newPassword
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder={t(
                            "change.password.newPassword.placeholder"
                          )}
                        />
                        {errors.newPassword && touched.newPassword ? (
                          <p
                            className="form-error-text paragraph-small-form"
                            role="alert"
                            id={"newPassword-error"}
                          >
                            {errors.newPassword}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="col-lg-12 col-md-12">
                      <div className="form-group">
                        <label
                          className="paragraph-small-fs9"
                          htmlFor={"newPasswordAgain"}
                        >
                          {t("change.password.confirmPassword")} *
                        </label>
                        <Field
                          name={"newPasswordAgain"}
                          type="password"
                          id={"newPasswordAgain"}
                          aria-describedby={
                            errors.newPasswordAgain && touched.newPasswordAgain
                              ? "newPasswordAgain-error"
                              : null
                          }
                          required={true}
                          className={
                            errors.newPasswordAgain && touched.newPasswordAgain
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder={t(
                            "change.password.confirmPassword.placeholder"
                          )}
                        />
                        {errors.newPasswordAgain && touched.newPasswordAgain ? (
                          <p
                            className="form-error-text paragraph-small-form"
                            role="alert"
                            id={"newPasswordAgain-error"}
                          >
                            {errors.newPasswordAgain}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className={"row footer-form-actions"}>
                    <div className={"col text-center"}>
                      <button type="submit" className={"btn btn-primary"}>
                        {t("common.updateBtn")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {open ? (
          <PasswordModalInfoMatch show={open} onClose={() => setOpen(false)} />
        ) : null}
      </div>
    </div>
  );
};

export default ChangePasswordChallenge;
